import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Ban, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { api, inr, fmtDate, fmtErr } from "@/lib/api";
import { CopyButton } from "@/components/CopyButton";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("submissions");

  const load = (q = search) => api.get(`/admin/users${q ? `?search=${encodeURIComponent(q)}` : ""}`).then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(""); }, []);

  const openDetail = async (u) => {
    try {
      const { data } = await api.get(`/admin/users/${u.id}`);
      setDetail(data);
      setTab("submissions");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const setStatus = async (u, status) => {
    try {
      await api.post(`/admin/users/${u.id}/status`, { status });
      toast.success(status === "suspended" ? "User suspended" : "User reactivated");
      if (detail?.id === u.id) setDetail({ ...detail, status });
      await load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="admin-users-page">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900">User Management</h1>
      <p className="mt-1 text-sm text-slate-600">Search, inspect and manage platform users.</p>

      <div className="mt-5 relative max-w-md">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input data-testid="admin-user-search" value={search} onChange={(e) => { setSearch(e.target.value); load(e.target.value); }}
          className="w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Search by name, email, phone or user ID" />
      </div>

      {!users ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : (
        <div className="mt-5 bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="admin-users-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">User ID</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Name</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Email</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Phone</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Joined</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Completed</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Total Earned</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.user_code}`} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3.5 font-medium text-slate-900">{u.user_code}</td>
                  <td className="px-4 py-3.5 font-medium text-slate-800">{u.name}</td>
                  <td className="px-4 py-3.5 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3.5 text-slate-600">{u.phone}</td>
                  <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3.5 text-slate-800">{u.tasks_completed}</td>
                  <td className="px-4 py-3.5 font-semibold text-blue-700">{inr(u.total_earned)}</td>
                  <td className="px-4 py-3.5"><StatusBadge status={u.status} /></td>
                  <td className="px-4 py-3.5 text-right">
                    <button onClick={() => openDetail(u)} data-testid={`view-user-${u.user_code}`} className="px-4 py-2 rounded-lg text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 transition-colors">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <p className="p-8 text-center text-sm text-slate-500" data-testid="users-empty">No users found.</p>}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">{detail.name} <StatusBadge status={detail.status} /></DialogTitle>
              </DialogHeader>
              <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-700 space-y-1">
                <p><span className="font-semibold">ID:</span> {detail.user_code} · <span className="font-semibold">Email:</span> {detail.email}</p>
                <p className="flex items-center flex-wrap gap-x-1"><span className="font-semibold">Phone:</span> {detail.phone} · <span className="font-semibold">UPI:</span> {detail.upi_id} <CopyButton value={detail.upi_id} testid="copy-upi-user-detail" label /></p>
                <p><span className="font-semibold">Joined:</span> {fmtDate(detail.created_at)}</p>
              </div>

              <div className="flex gap-2 mt-4" data-testid="user-detail-tabs">
                {["submissions", "rewards", "withdrawals"].map((t) => (
                  <button key={t} data-testid={`user-tab-${t}`} onClick={() => setTab(t)}
                    className={`px-4 py-2 rounded-xl text-xs font-semibold capitalize ${tab === t ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                    {t} ({detail[t]?.length || 0})
                  </button>
                ))}
              </div>

              <div className="mt-4 space-y-2.5 max-h-72 overflow-y-auto" data-testid="user-detail-list">
                {tab === "submissions" && (detail.submissions?.length ? detail.submissions.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-4 py-3 text-sm">
                    <div><p className="font-medium text-slate-900">{s.task_name}</p><p className="text-xs text-slate-500">{s.submission_code} · {fmtDate(s.created_at)}</p></div>
                    <StatusBadge status={s.status} />
                  </div>
                )) : <p className="text-sm text-slate-500 py-4 text-center">No submissions.</p>)}
                {tab === "rewards" && (detail.rewards?.length ? detail.rewards.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-4 py-3 text-sm">
                    <div><p className="font-medium text-slate-900">{inr(r.amount)}</p><p className="text-xs text-slate-500">{r.reward_code} · {r.task_code} · {fmtDate(r.created_at)}</p></div>
                    <StatusBadge status={r.status} />
                  </div>
                )) : <p className="text-sm text-slate-500 py-4 text-center">No rewards.</p>)}
                {tab === "withdrawals" && (detail.withdrawals?.length ? detail.withdrawals.map((w) => (
                  <div key={w.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-4 py-3 text-sm">
                    <div><p className="font-medium text-slate-900">{inr(w.amount)}</p><p className="text-xs text-slate-500">{w.withdrawal_code} · {w.upi_id} · {fmtDate(w.requested_at)}</p></div>
                    <StatusBadge status={w.status} />
                  </div>
                )) : <p className="text-sm text-slate-500 py-4 text-center">No withdrawals.</p>)}
              </div>

              <div className="pt-3 border-t border-slate-100 mt-2">
                {detail.status === "active" ? (
                  <button onClick={() => setStatus(detail, "suspended")} data-testid="suspend-user-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors">
                    <Ban className="w-4 h-4" /> Suspend User
                  </button>
                ) : (
                  <button onClick={() => setStatus(detail, "active")} data-testid="reactivate-user-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors">
                    <CheckCircle2 className="w-4 h-4" /> Reactivate User
                  </button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
